import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FiAlertTriangle, FiArrowLeft, FiEdit2, FiExternalLink, FiFileText, FiImage, FiSave, FiTrash2, FiX } from 'react-icons/fi';
import { getProductImageUrl, useProductImageFallback } from '../../utils/productImages';
import ProductFeaturesEditor from '../../admin_components/ProductFeaturesEditor';
import { parseProductFeatures } from '../../components/ProductPage/productFeatureIcons';
import './AdminUI.css';

export default function AdminProductDetails() {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [fileData, setFileData] = useState({});
  const { product_name: productName } = useParams();
  const navigate = useNavigate();

  const productToForm = (data) => ({
    product_name: data.product_name || '',
    product_description: data.product_description || '',
    product_price: data.product_price ?? '',
    product_brand: data.product_brand || '',
    product_category: data.product_category || '',
    product_stock: data.product_stock ?? '',
    product_rank: data.product_rank || '',
    published: data.published === 'Yes' ? 'true' : 'false',
    product_features: parseProductFeatures(data.product_features),
  });

  useEffect(() => {
    setLoading(true);
    fetch(`https://valleyapi-dr9y.onrender.com/api/products/${encodeURIComponent(productName)}`)
      .then((response) => response.json())
      .then((data) => {
        if (data.error) setProduct(null);
        else {
          setProduct(data);
          setFormData(productToForm(data));
        }
      })
      .catch((fetchError) => {
        console.error("Error fetching product:", fetchError);
        setProduct(null);
      })
      .finally(() => setLoading(false));
  }, [productName]);

  const checkAuth = () => {
    const token = localStorage.getItem('admin_token');
    if (!token || token === 'null') {
      alert("You are not authenticated. Please log in.");
      navigate('/admin-login');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!checkAuth() || !window.confirm("Save the changes to this product?")) return;
    const form = new FormData();
    Object.keys(formData).forEach((key) => form.append(key, key === 'product_features' ? JSON.stringify(formData[key]) : formData[key]));
    Object.keys(fileData).forEach((key) => fileData[key] && form.append(key, fileData[key]));
    setSaving(true);
    try {
      const response = await fetch(`https://valleyapi-dr9y.onrender.com/api/products/${encodeURIComponent(productName)}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
        body: form,
      });
      const data = await response.json();
      if (response.ok) {
        setProduct((current) => ({ ...current, ...formData, published: formData.published === 'true' ? 'Yes' : 'No' }));
        setEditMode(false);
        setFileData({});
        alert("Product updated successfully");
      } else if (response.status === 401) {
        alert("Session expired. Please log in again.");
        navigate('/admin-login');
      } else if (data.error?.includes('Product name must be unique')) {
        alert("Product name is already in use.");
      } else alert(`Failed to update product: ${data.error}`);
    } catch (updateError) {
      console.error("Error updating product:", updateError);
      alert("Error updating product. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setFileData({});
    setFormData(productToForm(product));
  };

  const handleDelete = async () => {
    if (!checkAuth() || !window.confirm(`Delete “${product.product_name}”? This action cannot be undone.`)) return;
    setSaving(true);
    try {
      const response = await fetch(`https://valleyapi-dr9y.onrender.com/api/products/${encodeURIComponent(productName)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
      });
      const data = await response.json();
      if (response.ok) navigate('/admin/products');
      else if (response.status === 401) navigate('/admin-login');
      else alert(`Failed to delete product: ${data.error}`);
    } catch (deleteError) {
      console.error("Error deleting product:", deleteError);
      alert("Error deleting product. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const updateField = (event) => setFormData((current) => ({ ...current, [event.target.name]: event.target.value }));
  const updateFile = (event) => setFileData((current) => ({ ...current, [event.target.name]: event.target.files[0] }));

  if (loading) return <div className="va-page va-card va-loading"><div className="va-spinner" aria-label="Loading product" /></div>;
  if (!product) return <div className="va-page va-card va-state"><div><FiAlertTriangle /><strong>Product not found</strong><p>The product may have been removed or renamed.</p><button className="va-button va-button--secondary" style={{ marginTop: 16 }} onClick={() => navigate('/admin/products')}><FiArrowLeft /> Back to products</button></div></div>;

  const documents = [
    { label: 'Technical document 1', value: product.product_pdf },
    { label: 'Technical document 2', value: product.product_pdf2 },
    { label: 'Technical document 3', value: product.product_pdf3 },
  ];

  return (
    <div className="va-page">
      <header className="va-page-header">
        <div>
          <span className="va-eyebrow">Product record · #{product.idproducts}</span>
          <h1 className="va-title">{product.product_name}</h1>
          <p className="va-subtitle">Review catalog information, inventory, pricing and attached technical documents.</p>
        </div>
        <div className="va-header-actions">
          {editMode ? (
            <><button className="va-button va-button--secondary" onClick={handleCancel} disabled={saving}><FiX /> Cancel</button><button className="va-button" onClick={handleSave} disabled={saving}><FiSave /> {saving ? 'Saving...' : 'Save changes'}</button></>
          ) : (
            <><button className="va-button va-button--secondary" onClick={() => navigate('/admin/products')}><FiArrowLeft /> Products</button><button className="va-button" onClick={() => setEditMode(true)}><FiEdit2 /> Edit product</button></>
          )}
        </div>
      </header>

      {editMode ? (
        <div className="va-detail-grid">
          <section className="va-card">
            <div className="va-card__header"><div><h2>Edit product information</h2><p>Update the commercial and catalog details below.</p></div></div>
            <div className="va-card__body">
              <div className="va-form-grid">
                <div className="va-field va-field--full"><label htmlFor="edit-name">Product name</label><input id="edit-name" className="va-input" name="product_name" value={formData.product_name} onChange={updateField} required /></div>
                <div className="va-field"><label htmlFor="edit-brand">Brand</label><input id="edit-brand" className="va-input" name="product_brand" value={formData.product_brand} onChange={updateField} required /></div>
                <div className="va-field"><label htmlFor="edit-category">Category</label><input id="edit-category" className="va-input" name="product_category" value={formData.product_category} onChange={updateField} required /></div>
                <div className="va-field"><label htmlFor="edit-price">Price</label><input id="edit-price" className="va-input" type="number" step="0.01" min="0" name="product_price" value={formData.product_price} onChange={updateField} /></div>
                <div className="va-field"><label htmlFor="edit-stock">Stock</label><input id="edit-stock" className="va-input" type="number" min="0" name="product_stock" value={formData.product_stock} onChange={updateField} /></div>
                <div className="va-field"><label htmlFor="edit-rank">Display rank</label><input id="edit-rank" className="va-input" type="number" min="0" name="product_rank" value={formData.product_rank} onChange={updateField} /></div>
                <div className="va-field"><label htmlFor="edit-published">Publication status</label><select id="edit-published" className="va-input" name="published" value={formData.published} onChange={updateField}><option value="true">Published</option><option value="false">Draft</option></select></div>
                <div className="va-field va-field--full"><label htmlFor="edit-description">Description</label><textarea id="edit-description" className="va-textarea" name="product_description" value={formData.product_description} onChange={updateField} required /></div>
              </div>
            </div>
          </section>

          <section className="va-card">
            <div className="va-card__header"><div><h2>Replace media</h2><p>Only selected files will be replaced.</p></div><span className="va-stat__icon"><FiImage /></span></div>
            <div className="va-card__body"><div className="va-form-grid va-form-grid--single">
              <div className="va-field"><label htmlFor="edit-image">Product image</label><input id="edit-image" className="va-input" type="file" name="product_image" accept="image/jpeg,image/jpg,image/png,image/webp" onChange={updateFile} /><small>{fileData.product_image?.name || 'Leave empty to keep the current image.'}</small></div>
              {['product_pdf', 'product_pdf2', 'product_pdf3'].map((name, index) => <div className="va-field" key={name}><label htmlFor={name}>Technical document {index + 1}</label><input id={name} className="va-input" type="file" name={name} accept="application/pdf" onChange={updateFile} /><small>{fileData[name]?.name || 'Leave empty to keep the current document.'}</small></div>)}
            </div></div>
          </section>

          <section className="va-card va-product-features-card">
            <div className="va-card__header"><div><h2>Highlighted functions</h2><p>Optional functions shown with symbols on the public product page.</p></div></div>
            <div className="va-card__body"><ProductFeaturesEditor features={formData.product_features || []} onChange={(features) => setFormData((current) => ({ ...current, product_features: features }))} /></div>
          </section>
        </div>
      ) : (
        <div className="va-detail-grid">
          <section className="va-card">
            <div className="va-card__header"><div><h2>Product information</h2><p>Catalog and commercial details.</p></div><span className={`va-badge ${product.published === 'Yes' ? 'va-badge--success' : 'va-badge--warning'}`}>{product.published === 'Yes' ? 'Published' : 'Draft'}</span></div>
            <div className="va-card__body">
              <div className="va-data-grid">
                <div className="va-data-item"><span>Brand</span><strong>{product.product_brand || 'Not specified'}</strong></div>
                <div className="va-data-item"><span>Category</span><strong>{product.product_category || 'Not specified'}</strong></div>
                <div className="va-data-item"><span>Price</span><strong>${Number(product.product_price || 0).toFixed(2)}</strong></div>
                <div className="va-data-item"><span>Inventory</span><strong>{product.product_stock ?? 0} units</strong></div>
                <div className="va-data-item"><span>Display rank</span><strong>{product.product_rank || 'Not ranked'}</strong></div>
                <div className="va-data-item"><span>Product ID</span><strong>#{product.idproducts}</strong></div>
                <div className="va-data-item va-data-item--full"><span>Description</span><p>{product.product_description || 'No description has been added.'}</p></div>
              </div>
            </div>
          </section>

          <div>
            <section className="va-card">
              <div className="va-card__header"><div><h2>Product image</h2></div><FiImage /></div>
              <div className="va-card__body">
                <div className="va-product-media">
                  <img src={getProductImageUrl(product.product_image)} alt={product.product_name} onError={useProductImageFallback} />
                </div>
              </div>
            </section>

            <section className="va-card">
              <div className="va-card__header"><div><h2>Documents</h2><p>Technical resources for this product.</p></div><FiFileText /></div>
              <div className="va-card__body">
                <div className="va-file-list">
                  {documents.some((document) => document.value && document.value !== 'none') ? documents.filter((document) => document.value && document.value !== 'none').map((document) => (
                    <a key={document.label} href={`https://valleyapi-dr9y.onrender.com/data/${document.value}`} className="va-file-link" target="_blank" rel="noopener noreferrer"><FiFileText /> <span>{document.label}</span><FiExternalLink style={{ marginLeft: 'auto' }} /></a>
                  )) : <div className="va-empty" style={{ minHeight: 120, padding: 10 }}><div><FiFileText /><strong>No documents</strong><p>Add PDFs while editing.</p></div></div>}
                </div>
              </div>
            </section>
          </div>
        </div>
      )}

      {!editMode && <div className="va-form-actions"><button className="va-button va-button--danger" onClick={handleDelete} disabled={saving}><FiTrash2 /> Delete product</button></div>}
    </div>
  );
}
